import { resolveGatewayBackedServiceBaseUrl } from "../services/serviceBaseUrl";

const MEDIA_SERVICE_BASE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_MEDIA_SERVICE_URL);

const ABSOLUTE_URL_PATTERN = /^(?:https?:|data:|blob:)/i;
const PROFILE_PICTURE_PATH_SEGMENT = "/api/media/profile-picture";
const PROFILE_COVER_PATH_SEGMENT = "/api/media/profile-cover";
const PUBLIC_PROFILE_PICTURE_PATH_SEGMENT = "/api/media/public/profile-picture";
const PUBLIC_PROFILE_COVER_PATH_SEGMENT = "/api/media/public/profile-cover";
const BAND_IMAGE_PATH_SEGMENT = "/api/media/bands/";
const PUBLIC_BAND_IMAGE_PATH_SEGMENT = "/api/media/public/bands/";
const POST_ATTACHMENT_PATH_SEGMENT = "/api/media/posts/";
const PUBLIC_POST_ATTACHMENT_PATH_SEGMENT = "/api/media/public/posts/";
const BAND_IMAGE_PATH_PATTERN = /^\/api\/media\/bands\/([^/?#]+)\/image(?=$|[?#])/;
const PUBLIC_BAND_IMAGE_PATH_PATTERN = /^\/api\/media\/public\/bands\/[^/?#]+\/image(?=$|[?#])/;
const POST_ATTACHMENT_PATH_PATTERN = /^\/api\/media\/posts\/([^/?#]+)\/download(?=$|[?#])/;
const PUBLIC_POST_ATTACHMENT_PATH_PATTERN = /^\/api\/media\/public\/posts\/[^/?#]+\/download(?=$|[?#])/;

export const resolveMediaUrl = (url) => {
    if (!url || typeof url !== "string") {
        return "";
    }
    if (ABSOLUTE_URL_PATTERN.test(url)) {
        return url;
    }
    const normalizedBase = MEDIA_SERVICE_BASE_URL;
    const normalizedPath = url.startsWith("/") ? url : `/${url}`;
    return `${normalizedBase}${normalizedPath}`;
};

export const getMediaServiceBaseUrl = () => MEDIA_SERVICE_BASE_URL;

export const isAbsoluteHttpUrl = (url) => {
    if (!url || typeof url !== "string") {
        return false;
    }
    try {
        const parsed = new URL(url.trim());
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
};

const normalizeMediaPath = (url, protectedPathSegment, publicPathSegment) => {
    if (!url || typeof url !== "string") {
        return null;
    }
    const trimmed = url.trim();
    if (!trimmed) {
        return null;
    }
    if (trimmed.startsWith("/")) {
        return trimmed;
    }
    try {
        const parsed = new URL(trimmed);
        const pathAndSearch = `${parsed.pathname}${parsed.search}`;
        if (
            pathAndSearch.startsWith(protectedPathSegment)
            || pathAndSearch.startsWith(publicPathSegment)
        ) {
            return pathAndSearch;
        }
        return trimmed;
    } catch {
        return trimmed;
    }
};

const toPublicProfileMediaUrl = (url, protectedPathSegment, publicPathSegment) => {
    const normalized = normalizeMediaPath(url, protectedPathSegment, publicPathSegment);
    if (!normalized) {
        return null;
    }
    if (normalized.startsWith(publicPathSegment)) {
        return normalized;
    }
    if (normalized.startsWith(protectedPathSegment)) {
        return `${publicPathSegment}${normalized.slice(protectedPathSegment.length)}`;
    }
    return normalized;
};

export const toPublicProfilePictureUrl = (url) => (
    toPublicProfileMediaUrl(url, PROFILE_PICTURE_PATH_SEGMENT, PUBLIC_PROFILE_PICTURE_PATH_SEGMENT)
);

export const toPublicProfileCoverUrl = (url) => (
    toPublicProfileMediaUrl(url, PROFILE_COVER_PATH_SEGMENT, PUBLIC_PROFILE_COVER_PATH_SEGMENT)
);

export const isProfilePictureMediaUrl = (url) => {
    const normalized = normalizeMediaPath(url, PROFILE_PICTURE_PATH_SEGMENT, PUBLIC_PROFILE_PICTURE_PATH_SEGMENT);
    return Boolean(
        normalized
        && (
            normalized.startsWith(PROFILE_PICTURE_PATH_SEGMENT)
            || normalized.startsWith(PUBLIC_PROFILE_PICTURE_PATH_SEGMENT)
        )
    );
};

export const toPublicBandImageUrl = (url) => {
    const normalized = normalizeMediaPath(url, BAND_IMAGE_PATH_SEGMENT, PUBLIC_BAND_IMAGE_PATH_SEGMENT);
    if (!normalized) {
        return null;
    }
    if (PUBLIC_BAND_IMAGE_PATH_PATTERN.test(normalized)) {
        return normalized;
    }
    const protectedMatch = normalized.match(BAND_IMAGE_PATH_PATTERN);
    if (!protectedMatch) {
        return normalized;
    }
    return `${PUBLIC_BAND_IMAGE_PATH_SEGMENT}${protectedMatch[1]}/image${normalized.slice(protectedMatch[0].length)}`;
};

export const toPublicPostAttachmentUrl = (url) => {
    const normalized = normalizeMediaPath(url, POST_ATTACHMENT_PATH_SEGMENT, PUBLIC_POST_ATTACHMENT_PATH_SEGMENT);
    if (!normalized) {
        return null;
    }
    if (PUBLIC_POST_ATTACHMENT_PATH_PATTERN.test(normalized)) {
        return normalized;
    }
    const protectedMatch = normalized.match(POST_ATTACHMENT_PATH_PATTERN);
    if (!protectedMatch) {
        return normalized;
    }
    return `${PUBLIC_POST_ATTACHMENT_PATH_SEGMENT}${protectedMatch[1]}/download${normalized.slice(protectedMatch[0].length)}`;
};
